import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Alert, Container, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Action } from '@reduxjs/toolkit';
import {
  ActionButtons,
  addStatusToast,
  DeleteConfirmation,
  Loader,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS } from '../../common/constants';
import { initialTranslationsState } from '../../common/types/translations.types';
import {
  CountryDetails,
  deleteCountryAsync,
  getCountryDetailsAsync,
  saveCountryAsync,
  selectCountryDetails,
  selectCountryDetailsLoadingStatus,
  updateCountryAsync,
} from './countriesSlice';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';

function CountryAddOrUpdate() {
  const backLinkUrl = '/locations/countries';
  const params = useParams();
  const dispatch = useAppDispatch();
  const countryDetails = useAppSelector(selectCountryDetails);
  const countryDetailsLoadingStatus = useAppSelector(
    selectCountryDetailsLoadingStatus,
  );
  const inputErrors = useAppSelector(selectInputErrors);
  const isAddMode = !params.countryId;

  const initialCountryState: CountryDetails = {
    nameTranslations: initialTranslationsState,
  };

  const [country, setCountry] = useState(initialCountryState);
  const [countryNameFi, setCountryNameFi] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);

  const initFetch = useCallback(() => {
    dispatch(getCountryDetailsAsync(params.countryId));
  }, [dispatch, params.countryId]);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    if (!isAddMode) {
      initFetch();
    }
  }, [isAddMode, initFetch]);

  useEffect(() => {
    if (!isAddMode) {
      setCountry(countryDetails);
    }
  }, [isAddMode, countryDetails]);

  useEffect(() => {
    if (country.nameTranslations.length > 0) {
      const countryObject = country.nameTranslations.find(
        obj => obj.locale === 'fi_FI',
      );
      setCountryNameFi(countryObject.text);
    }
  }, [country]);

  const deleteCountry = () => {
    dispatch(deleteCountryAsync(params.countryId));
    setDeleted(true);
    setShowDeleteModal(false);
  };

  const handleTranslation = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { culture } = event.target.dataset;
    const textValue = event.target.value;
    const newCountry = { ...country };

    newCountry[finalProp] = country[finalProp].map(item => {
      if (item.locale === culture) {
        return { ...item, text: textValue };
      }
      return item;
    });
    setCountry(newCountry);
  };

  const addOrUpdateCountry = async () => {
    let actionResult: Action<string>;
    if (isAddMode) {
      actionResult = await dispatch(saveCountryAsync(country));
    } else {
      actionResult = await dispatch(updateCountryAsync(country));
    }

    if (
      saveCountryAsync.fulfilled.match(actionResult) ||
      updateCountryAsync.fulfilled.match(actionResult)
    ) {
      dispatch(
        addStatusToast({
          Message: `Maan ${countryNameFi} tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
    }
  };

  const generatePageTitle = function () {
    if (isAddMode) {
      return 'Uuden maan lisäys';
    }
    if (
      countryDetailsLoadingStatus === LOADING_STATUS.IDLE &&
      countryNameFi !== ''
    ) {
      return `Maan tietojen päivitys: ${countryNameFi}`;
    }
    return 'Maan tietojen päivitys';
  };

  return (
    <>
      <Helmet>
        <title>Sijaintitiedot - Maat</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Sijaintitiedot', 'Maat']}
          Title={generatePageTitle()}
        />

        <Loader
          IsLoading={countryDetailsLoadingStatus === LOADING_STATUS.LOADING}
        />

        {countryDetailsLoadingStatus === LOADING_STATUS.IDLE && (
          <Container className="container-form mt-48">
            {country.nameTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="nameTranslations"
                className="mb-40"
              >
                <Form.Label>Nimi ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="nameTranslations"
                />
              </Form.Group>
            ))}

            <InputErrorsDisplay inputErrors={inputErrors} />

            <ActionButtons
              CancelButtonText={
                deleted || submitted ? 'Palaa takaisin' : 'Peruuta'
              }
              CancelButtonUrl={backLinkUrl}
              DeleteButtonText={isAddMode || deleted ? '' : 'Poista maa'}
              HandleDelete={
                isAddMode || deleted ? null : () => setShowDeleteModal(true)
              }
              HandleSave={addOrUpdateCountry}
            />

            {(country.isDeleted || deleted) && (
              <Alert variant="danger">Maa on poistettu!</Alert>
            )}
          </Container>
        )}
      </Container>

      <DeleteConfirmation
        ConfirmDelete={deleteCountry}
        HideModal={() => setShowDeleteModal(false)}
        Id={country.id}
        Message={`Haluatko varmasti poistaa maan ${countryNameFi}?`}
        ShowModal={showDeleteModal}
      />
    </>
  );
}

export default CountryAddOrUpdate;
